import React, { useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";

// Admin-managed G.O.A.L category list. Add / rename / delete at any time.
export const GoalCategoriesDialog = ({ open, onOpenChange, categories, canEdit, onChanged }) => {
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await staffApi.post("/goal/categories", { name });
      setNewName("");
      toast.success(`Category "${name}" added`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const rename = async (cid) => {
    const name = editName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await staffApi.put(`/goal/categories/${cid}`, { name });
      setEditId(null);
      toast.success("Category updated");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const remove = async (cat) => {
    setBusy(true);
    try {
      await staffApi.delete(`/goal/categories/${cat.id}`);
      toast.success(`Category "${cat.name}" deleted`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="goal-categories-dialog">
        <DialogHeader>
          <DialogTitle>G.O.A.L categories</DialogTitle>
          <DialogDescription>
            Add, rename or remove the categories the team can pick from. A category already used by a
            record can be renamed but not deleted.
          </DialogDescription>
        </DialogHeader>

        {canEdit && (
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="New category name"
              data-testid="goal-category-new-input"
            />
            <Button onClick={add} disabled={busy || !newName.trim()} data-testid="goal-category-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        )}

        <div className="max-h-[340px] overflow-y-auto divide-y divide-border rounded-md border border-border">
          {categories.map((c) => (
            <div key={c.id} className="flex items-center gap-2 px-3 py-2" data-testid={`goal-category-row-${c.id}`}>
              {editId === c.id ? (
                <>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && rename(c.id)}
                    className="h-8"
                    autoFocus
                    data-testid={`goal-category-edit-input-${c.id}`}
                  />
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => rename(c.id)} data-testid={`goal-category-save-${c.id}`}>
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditId(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm">{c.name}</span>
                  {canEdit && (
                    <>
                      <Button
                        size="icon" variant="ghost" className="h-8 w-8"
                        onClick={() => { setEditId(c.id); setEditName(c.name); }}
                        data-testid={`goal-category-edit-${c.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="icon" variant="ghost" className="h-8 w-8 text-destructive"
                        onClick={() => remove(c)}
                        data-testid={`goal-category-delete-${c.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
          {categories.length === 0 && (
            <p className="px-3 py-6 text-sm text-muted-foreground text-center">No categories yet.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GoalCategoriesDialog;
